import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "loadingOverlay",
    "exportContent",
    "checkbox",
    "startDate",
    "endDate",
    "siteCheckbox",
    "siteItem",
    "employeeItem",
    "errorMessage",
    "errorText",
    "successMessage",
    "siteFilterEnabled",
    "siteFilterContent",
    "teamCheckbox",
    "teamItem",
    "teamFilterEnabled",
    "teamFilterContent",
    "showConflicts",
    "traineeReportButton",
    "statusCheckbox",
    "subjectLabel",
    "teamSection",
    "employeeSection",
    "fleetSection",
    "fleetCheckbox",
    "fleetItem",
    "employeeFormats",
    "fleetFormats",
  ]

  // Subject toggle styling, kept in sync with configurable_timesheet_filter_controller.
  static SUBJECT_ACTIVE_CLASSES = ["bg-green-600", "text-white", "font-medium"]
  static SUBJECT_INACTIVE_CLASSES = ["bg-white", "text-gray-700", "hover:bg-gray-50"]

  static values = {
    excelUrl: String,
    excelDailyUrl: String,
    csvUrl: String,
    pdfZipUrl: String,
    gdiCsvUrl: String,
    addisonCsvUrl: String,
    lohnabrechnungXlsxUrl: String,
    teamUrl: String,
    brzUrl: String,
    traineeReportUrl: String,
    monthlyOverviewUrl: String,
    exportsUrl: String,
    noEmployeesText: String,
    fleetCsvUrl: String,
    fleetExcelUrl: String,
    noFleetsText: String,
  }

  connect() {
    if (this.hasErrorTextTarget) {
      this.defaultErrorText = this.errorTextTarget.textContent
    }
    this.employeeQuery = ""
    // Read the mode off the DOM rather than assuming "employee": a Turbo cache
    // restore brings back the previously checked radio, and a hardcoded default
    // would disagree with what the user sees.
    this.subject = this.checkedSubject()
    // Reset dialog state on connect (handles Turbo cache restoration)
    this.resetDialogState()
    this.updateEmployeeVisibility()
    this.applySubjectMode()
  }

  checkedSubject() {
    const checked = this.element.querySelector("input[name='export_subject']:checked")
    return checked ? checked.value : "employee"
  }

  selectSubject(event) {
    this.subject = event.target.value
    this.hideError()
    this.applySubjectMode()
  }

  // Swaps the whole left-column roster and the format button stack. No-ops for
  // tenants without fleet templates, where none of these targets exist.
  applySubjectMode() {
    const fleetMode = this.subject === "fleet"

    const { SUBJECT_ACTIVE_CLASSES, SUBJECT_INACTIVE_CLASSES } = this.constructor
    this.subjectLabelTargets.forEach((label) => {
      const input = label.querySelector("input[type=radio]")
      const active = Boolean(input) && input.value === this.subject
      SUBJECT_ACTIVE_CLASSES.forEach((cls) => label.classList.toggle(cls, active))
      SUBJECT_INACTIVE_CLASSES.forEach((cls) => label.classList.toggle(cls, !active))
    })

    if (this.hasTeamSectionTarget) this.teamSectionTarget.classList.toggle("hidden", fleetMode)
    if (this.hasEmployeeSectionTarget)
      this.employeeSectionTarget.classList.toggle("hidden", fleetMode)
    if (this.hasEmployeeFormatsTarget)
      this.employeeFormatsTarget.classList.toggle("hidden", fleetMode)
    if (this.hasFleetSectionTarget) this.fleetSectionTarget.classList.toggle("hidden", !fleetMode)
    if (this.hasFleetFormatsTarget) this.fleetFormatsTarget.classList.toggle("hidden", !fleetMode)
  }

  resetDialogState() {
    // Hide loading overlay
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
    }
    // Hide success message
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    // Hide error message
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
    // Show export content
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("hidden", "opacity-50", "pointer-events-none")
    }
  }

  openDialog() {
    const dialog = document.getElementById("configurable-timesheet-export-dialog")
    if (dialog) {
      dialog.showModal()
      document.body.style.overflow = "hidden"
    }
  }

  searchSites(event) {
    const query = event.target.value.toLowerCase().trim()
    this.siteItemTargets.forEach((item) => {
      const name = item.dataset.name.toLowerCase()
      item.classList.toggle("hidden", query && !name.includes(query))
    })
  }

  searchEmployees(event) {
    this.employeeQuery = event.target.value.toLowerCase().trim()
    this.updateEmployeeVisibility()
  }

  searchTeams(event) {
    const query = event.target.value.toLowerCase().trim()
    this.teamItemTargets.forEach((item) => {
      const name = item.dataset.name.toLowerCase()
      item.classList.toggle("hidden", query && !name.includes(query))
    })
  }

  // Search and the team filter are two independent reasons to hide an employee row,
  // so both are recomputed here rather than each toggling "hidden" on its own.
  // Only the team filter excludes an employee from the export — search stays purely
  // visual, as it always has.
  updateEmployeeVisibility() {
    const teamFilterActive = this.hasTeamFilterEnabledTarget && this.teamFilterEnabledTarget.checked
    const selectedTeams = new Set(
      this.teamCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value),
    )

    this.employeeItemTargets.forEach((item) => {
      const teamIds = (item.dataset.teamIds || "").split(",").filter(Boolean)
      const matchesTeam = !teamFilterActive || teamIds.some((id) => selectedTeams.has(id))
      const matchesSearch =
        !this.employeeQuery || item.dataset.name.toLowerCase().includes(this.employeeQuery)

      item.classList.toggle("hidden", !(matchesTeam && matchesSearch))

      const checkbox = item.querySelector("input[type=checkbox]")
      if (checkbox) {
        checkbox.dataset.teamExcluded = String(teamFilterActive && !matchesTeam)
      }
    })

    this.updateTraineeButton()
  }

  // Employees the export will actually include: checked and not filtered out by team.
  selectedEmployeeCheckboxes() {
    return this.checkboxTargets.filter((cb) => cb.checked && cb.dataset.teamExcluded !== "true")
  }

  selectAll(event) {
    event.preventDefault()
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = true
    })
    this.updateTraineeButton()
  }

  deselectAll(event) {
    event.preventDefault()
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
    this.updateTraineeButton()
  }

  // Show the Berichtsheft button only when at least one selected employee is a
  // trainee. The button is only present in the DOM when the role permits it.
  updateTraineeButton() {
    if (!this.hasTraineeReportButtonTarget) return

    const anyTraineeSelected = this.selectedEmployeeCheckboxes().some(
      (checkbox) => checkbox.dataset.trainee === "true",
    )
    this.traineeReportButtonTarget.classList.toggle("hidden", !anyTraineeSelected)
  }

  selectAllSites(event) {
    event.preventDefault()
    this.siteCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = true
    })
  }

  deselectAllSites(event) {
    event.preventDefault()
    this.siteCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
  }

  toggleSiteFilter() {
    if (this.hasSiteFilterContentTarget) {
      const enabled = this.siteFilterEnabledTarget.checked
      this.siteFilterContentTarget.classList.toggle("hidden", !enabled)
    }
  }

  selectAllTeams(event) {
    event.preventDefault()
    this.teamCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = true
    })
    this.updateEmployeeVisibility()
  }

  deselectAllTeams(event) {
    event.preventDefault()
    this.teamCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
    this.updateEmployeeVisibility()
  }

  searchFleets(event) {
    const query = event.target.value.toLowerCase().trim()
    this.fleetItemTargets.forEach((item) => {
      const name = item.dataset.name.toLowerCase()
      item.classList.toggle("hidden", Boolean(query) && !name.includes(query))
    })
  }

  selectedFleetCheckboxes() {
    return this.fleetCheckboxTargets.filter((cb) => cb.checked)
  }

  selectAllFleets(event) {
    event.preventDefault()
    this.fleetCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = true
    })
  }

  deselectAllFleets(event) {
    event.preventDefault()
    this.fleetCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
  }

  toggleTeamFilter() {
    if (this.hasTeamFilterContentTarget) {
      const enabled = this.teamFilterEnabledTarget.checked
      this.teamFilterContentTarget.classList.toggle("hidden", !enabled)
    }
    this.updateEmployeeVisibility()
  }

  applyTeamFilter() {
    this.updateEmployeeVisibility()
  }

  exportExcel(event) {
    event.preventDefault()
    this.performExport(this.excelUrlValue, "xlsx")
  }

  exportExcelDaily(event) {
    event.preventDefault()
    this.performExport(this.excelDailyUrlValue, "xlsx")
  }

  exportPdfZip(event) {
    event.preventDefault()
    this.performExport(this.pdfZipUrlValue, "zip")
  }

  exportMonthlyOverview(event) {
    event.preventDefault()
    this.performExport(this.monthlyOverviewUrlValue, "zip")
  }

  exportCsv(event) {
    event.preventDefault()
    this.performExport(this.csvUrlValue, "csv")
  }

  exportGdiCsv(event) {
    event.preventDefault()
    this.performExport(this.gdiCsvUrlValue, "csv")
  }

  exportAddisonCsv(event) {
    event.preventDefault()
    this.performExport(this.addisonCsvUrlValue, "txt")
  }

  exportLohnabrechnungXlsx(event) {
    event.preventDefault()
    this.performExport(this.lohnabrechnungXlsxUrlValue, "xlsx")
  }

  exportTeam(event) {
    event.preventDefault()
    this.performExport(this.teamUrlValue, "zip")
  }

  exportBrz(event) {
    event.preventDefault()
    this.performExport(this.brzUrlValue, "csv")
  }

  exportTraineeReport(event) {
    event.preventDefault()
    this.performExport(this.traineeReportUrlValue, "zip")
  }

  exportFleetCsv(event) {
    event.preventDefault()
    this.performExport(this.fleetCsvUrlValue, "csv")
  }

  exportFleetExcel(event) {
    event.preventDefault()
    this.performExport(this.fleetExcelUrlValue, "xlsx")
  }

  async performExport(baseUrl, format) {
    this.hideError()
    this.hideSuccess()

    // Without this the request carries no subject ids at all, and the server falls
    // back to *every* employee (or vehicle) of the tenant — the opposite of what
    // was selected.
    if (this.subject === "fleet") {
      if (this.selectedFleetCheckboxes().length === 0) {
        this.showError(this.noFleetsTextValue)
        return
      }
    } else if (this.selectedEmployeeCheckboxes().length === 0) {
      this.showError(this.noEmployeesTextValue)
      return
    }

    // Show loading overlay
    this.showLoading()

    // Build FormData with parameters
    const formData = this.buildFormData(format)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(baseUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: formData,
      })

      const json = await response.json()

      if (json.error === "no_timesheets") {
        this.showError()
        return
      }

      if (json.success) {
        // Export started in background - show success message
        this.showSuccess()
        return
      }

      if (json.error) {
        console.error("Export failed:", json.error)
        this.showError(json.error)
        return
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error("Export failed:", error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  buildFormData(format) {
    const formData = new FormData()

    // Add format
    if (format) {
      formData.append("format", format)
    }

    // Add date range
    if (this.hasStartDateTarget && this.startDateTarget.value) {
      formData.append("q[reported_date_gteq]", this.startDateTarget.value)
    }
    if (this.hasEndDateTarget && this.endDateTarget.value) {
      formData.append("q[reported_date_lteq]", this.endDateTarget.value)
    }

    // Add selected sites (only if site filter is enabled)
    if (this.hasSiteFilterEnabledTarget && this.siteFilterEnabledTarget.checked) {
      const selectedSites = this.siteCheckboxTargets.filter((cb) => cb.checked)
      selectedSites.forEach((cb) => {
        formData.append("q[site_id_in][]", cb.value)
      })
    }

    // Subject-specific filters. Fleet exports have no team narrowing and no PDF, so
    // sending those params would only mislead the server about what was selected.
    if (this.subject === "fleet") {
      this.selectedFleetCheckboxes().forEach((cb) => {
        formData.append("q[fleet_id_in][]", cb.value)
      })
    } else {
      // Add selected teams (only if team filter is enabled) — the server re-applies the
      // constraint so the export is correct even if the client-side narrowing is bypassed.
      if (this.hasTeamFilterEnabledTarget && this.teamFilterEnabledTarget.checked) {
        this.teamCheckboxTargets
          .filter((cb) => cb.checked)
          .forEach((cb) => {
            formData.append("q[team_id_in][]", cb.value)
          })
      }

      // Add selected employees
      this.selectedEmployeeCheckboxes().forEach((cb) => {
        formData.append("q[employee_id_in][]", cb.value)
      })

      // Add show_conflicts flag for PDF export
      if (this.hasShowConflictsTarget && this.showConflictsTarget.checked) {
        formData.append("show_conflicts", "1")
      }
    }

    // Add selected approval states (defaults to approved-only server-side when none selected)
    this.statusCheckboxTargets
      .filter((cb) => cb.checked)
      .forEach((cb) => {
        formData.append("q[state_in][]", cb.value)
      })

    return formData
  }

  showLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.add("opacity-50", "pointer-events-none")
    }
  }

  hideLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  showError(message = null) {
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message || this.defaultErrorText
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  showSuccess() {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.remove("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.add("hidden")
    }
  }

  hideSuccess() {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("hidden")
    }
  }

  closeDialog() {
    // Check if this is a dialog element - if so, close it using dialog API
    if (this.element.tagName === "DIALOG") {
      this.element.close()
      document.body.style.overflow = ""
      return
    }

    // Try to find the dialog controller on the same element and close it
    const dialogController = this.application.getControllerForElementAndIdentifier(
      this.element,
      "timesheet-dialog",
    )
    if (dialogController && typeof dialogController.close === "function") {
      dialogController.close()
      return
    }
  }
}
